import usePlaid from "./usePlaid";
import Typography from "@mui/material/Typography";
import OtherEmploymentDialog from "./OtherEmploymentDialog";
import CTrackerTimelineItem from "components/CTimelineItem";
import { Fragment, useEffect, useState } from "react";
import useTracker from "components/CTracker/useTrackerContext";
import TexasAppraisalDialog from "./TexasAppraisalDialog";
import CUploadDocumentDialog from "./AddTaxReturnsDialog";
import TruvBridge from "@truv/react";
import useTruvIncome from "./useTruvIncome";
import FsdAndConfScorePassed from "./confScoreAndFsdChecker";
import { useTheme } from "@mui/material";
import { TrackerStatusColor } from "typedef/TrackerStatusColor";
import useTWNVerification from "./TWN/useTWNVerification";
import toPercentage from "utils/toPercentage";
import CDocUploadDocumentDialog from "./AddMissingDocsDialog";

const IncomeVerification: React.FC<{
  trackerStatusColor: TrackerStatusColor;
}> = ({ trackerStatusColor }) => {
  const [openTruv, setOpenTruv] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [openDialogAppraisal, setOpenDialogAppraisal] = useState(false);
  const [showVerifyButton, setShowVerifyButton] = useState(true);
  const [addTaxReturnsDialogOpen, setAddTaxReturnsDialogOpen] = useState(false);
  const [addDocsDialogOpen, setAddDocsDialogOpen] = useState(false);

  const { loan, type, isModalOpen, setIsModalOpen } = useTracker();

  const hasAppraisalType =
    loan?.borrowerSteps?.appraisalVerification?.appraisalType &&
    loan?.borrowerSteps?.appraisalVerification?.appraisalType?.length > 0 &&
    loan?.borrowerSteps?.appraisalVerification?.status !== "pending";

  const noNeedForAppraisal =
    !loan?.borrowerSteps?.appraisalVerification?.status;

  const homeValuationSuccess =
    loan?.borrowerSteps?.homeValuationVerification.status === "success";

  useEffect(() => {
    if (!noNeedForAppraisal && !hasAppraisalType && homeValuationSuccess) {
      setOpenDialogAppraisal(true);
    } else setOpenDialogAppraisal(false);
  }, [hasAppraisalType, noNeedForAppraisal, homeValuationSuccess]);

  const theme = useTheme();
  const plaid = usePlaid();
  const truv = useTruvIncome();

  const { getTWNIncome } = useTWNVerification();
  const isBorrower = type === "borrower";
  const employmentType = isBorrower
    ? loan?.borrowerEmploymentType
    : loan?.coborrowerEmploymentType;
  const isSelfEmployed = employmentType === "Self Employed";
  const step = isBorrower ? loan?.borrowerSteps : loan?.coborrowerSteps;
  const message = step?.incomeVerification?.message ?? "";
  const isTX = loan?.property?.address?.components?.state_abbreviation === "TX";
  const cltvHigherThan70 =
    loan?.initialOffer.cltv && loan?.initialOffer.cltv >= 0.7 ? true : false;
  const isTXCLTVHigherThan70 = isTX && cltvHigherThan70;
  const promptManualTruv = step?.incomeVerification.promptManualTruv;
  const docsUploadedByBorrower =
    step?.incomeVerification?.docsUploadedByBorrower;
  const neededDocs = step?.incomeVerification.missingDocs;

  const prevStep = isBorrower
    ? (hasAppraisalType || noNeedForAppraisal) &&
      step?.creditVerification.status === "success"
    : step?.creditVerification.status === "success";
  const status = prevStep
    ? step?.incomeVerification?.status ?? "pending"
    : "pending";

  const appraisalPending =
    loan?.borrowerSteps?.appraisalVerification?.status === "analyzing";
  const isCreditVerificationSuccess =
    loan?.borrowerSteps?.creditVerification?.status === "success";

  const taxReturns = step?.incomeVerification.taxReturns;
  const bank = step?.incomeVerification?.bank;
  const documentsNeededToContinue = 2;
  const needTaxReturnsUpload = status === "analyzing" && isSelfEmployed && bank;
  const isB1Docs =
    loan?.borrowerSteps?.incomeVerification?.truvIncome?.paystub1 ||
    loan?.borrowerSteps?.incomeVerification?.truvIncome?.paystub2 ||
    loan?.borrowerSteps?.incomeVerification?.w2AdditionalIncome;
  const isB2Docs =
    loan?.coborrowerSteps?.incomeVerification?.truvIncome?.paystub1 ||
    loan?.coborrowerSteps?.incomeVerification?.truvIncome?.paystub2 ||
    loan?.coborrowerSteps?.incomeVerification?.w2AdditionalIncome;

  const annualIncomeInital = loan?.initialOffer?.annualIncome;
  const annualDebts = loan?.borrowerSteps?.creditVerification?.monthlyDebtLoad;
  const debtToIncomeRatioInitial = loan?.initialOffer?.debtToIncomeRatio;
  const debtToIncomeRatiofinal = loan?.finalOffer?.debtToIncomeRatio as number;
  const annualIncome = loan?.borrowerSteps?.incomeVerification?.annualIncome;
  const maxDebtToIncomeRatio = 0.5;
  const debtToIncomeRatio = debtToIncomeRatiofinal ?? debtToIncomeRatioInitial;
  const incomeUpdate = !annualIncome
    ? (annualIncomeInital ?? 0).toLocaleString("en-US")
    : (annualIncome ?? 0).toLocaleString("en-US");
  const loanBalanceCredit = (annualDebts ? annualDebts * 12 : 0).toLocaleString(
    "en-US",
  );
  const incomeMessage =
    (debtToIncomeRatio ?? 0) <= maxDebtToIncomeRatio
      ? " acceptable!"
      : " too high. Our CX team will review options and contact you.";
  const showAddTaxReturns =
    needTaxReturnsUpload &&
    (!taxReturns || taxReturns?.length < documentsNeededToContinue);
  const isEnabledByLO = loan?.borrowerFlags?.enabledByLO === true;
  const plaidWarningFlag = loan?.borrowerFlags?.showedPlaidWarning;

  return (
    <CTrackerTimelineItem
      status={status}
      title="Income"
      loading={loading}
      tooltip={`For W2 applicants, we use a payroll verification system.  For self-employed applicants, we connect with your bank account to verify income.`}
      {...(showVerifyButton &&
        status === "analyzing" &&
        prevStep &&
        (noNeedForAppraisal || hasAppraisalType) &&
        !plaidWarningFlag && {
          action: {
            message: "Verify Now",
            onClick: async () => {
              if (isSelfEmployed) {
                plaid.loadEmployment();
              } else {
                setLoading(true);
                if (step?.incomeVerification?.needsReview) {
                  await truv.getBridgeToken();
                  setOpenTruv(true);
                } else {
                  const twnIncomeResponse =
                    loan && (await getTWNIncome(isBorrower, loan));
                  if (twnIncomeResponse && "error" in twnIncomeResponse) {
                    await truv.getBridgeToken();
                    setOpenTruv(true);
                  }
                }
                setShowVerifyButton(false);
                setLoading(false);
              }
            },
          },
        })}
      {...(promptManualTruv &&
        status === "analyzing" &&
        prevStep &&
        (noNeedForAppraisal || hasAppraisalType) &&
        !(isBorrower ? isB1Docs : isB2Docs) &&
        !isSelfEmployed && {
          action: {
            message: "Upload your docs here!",
            onClick: async () => {
              setLoading(true);
              await truv.getBridgeToken();
              setOpenTruv(true);
              setShowVerifyButton(false);
              setLoading(false);
            },
          },
        })}
      {...(docsUploadedByBorrower === false && {
        actions: [
          {
            message: "Upload them here!",
            onClick: () => setAddDocsDialogOpen(true),
          },
        ],
      })}
      {...(showAddTaxReturns && {
        actions: [
          {
            message: "Add Tax Returns",
            onClick: () => setAddTaxReturnsDialogOpen(true),
          },
        ],
      })}
    >
      <TruvBridge
        isOpened={openTruv}
        onClose={() => setOpenTruv(false)}
        bridgeToken={truv.bridgeToken as string}
        onSuccess={async (public_token: string) => {
          setOpenTruv(false);
          await truv.sendPublicToken(public_token);
        }}
        onEvent={() => setShowVerifyButton(false)}
      />

      <Typography variant="h6" color={[trackerStatusColor[status]]}>
        {status === "analyzing" &&
          !isBorrower &&
          step?.incomeVerification.message}
        {status === "failed" && message}
        {status === "success" && !isBorrower && message}
        {isBorrower &&
          status === "success" &&
          loan?.coborrowerInvite &&
          loan.coborrowerSteps?.incomeVerification.status !== "success" && (
            <Fragment>
              <br />
              Waiting on the borrower to complete their steps
            </Fragment>
          )}

        {isBorrower && (status === "analyzing" || status === "success") && (
          <Fragment>
            {status === "analyzing"
              ? `You reported $${incomeUpdate} of annual household income.`
              : `We verified $${incomeUpdate} of annual income.`}
            <br />
            We detected ${loanBalanceCredit} of annual debts from your credit
            report.
            <br />
            {((!step?.incomeVerification?.needsReview &&
              status === "analyzing") ||
              status === "success") && (
              <>
                The result is a Debt to Income ratio of{" "}
                {toPercentage(debtToIncomeRatio ?? 0)} which is {incomeMessage}
                <br />
              </>
            )}
            {status === "analyzing" && message}
            {status === "success" && message}
          </Fragment>
        )}

        {docsUploadedByBorrower === false &&
          neededDocs?.map((doc) => (
            <Typography fontSize={18} variant="h6" color={"#808080"}>
              {doc}
            </Typography>
          ))}
      </Typography>

      {needTaxReturnsUpload && (
        <Fragment>
          {showAddTaxReturns && (
            <Typography
              fontSize={18}
              variant="h6"
              color={theme.palette.primary.main}
            >
              Please upload your prior two year’s for review
            </Typography>
          )}
          {taxReturns
            ?.sort((a, b) => Number(a.taxYear) - Number(b.taxYear))
            .map((taxReturn) => {
              return (
                <Typography
                  key={taxReturn.taxYear}
                  fontSize={18}
                  variant="h6"
                  color={"#808080"}
                >
                  {`${taxReturn.taxYear}: Your tax return has been sucessfully received.`}
                </Typography>
              );
            })}
          {!showAddTaxReturns && (
            <Typography fontSize={18} variant="h6" color={"#808080"}>
              Thank you! Your documents are being reviewed.
            </Typography>
          )}
        </Fragment>
      )}

      <OtherEmploymentDialog
        open={
          (isBorrower && isSelfEmployed && plaid.otherEmploymentDialogIsOpen) ||
          (!isBorrower && isSelfEmployed && plaid.otherEmploymentDialogIsOpen)
        }
        onAccept={() => {
          plaid.setOtherEmploymentDialogIsOpen(false);
          plaid.loadEmployment();
        }}
        onDismiss={async () => {
          plaid.setOtherEmploymentDialogIsOpen(false);
          await plaid.setFlags({ offeredAddingEmployment: true });
        }}
      />

      <TexasAppraisalDialog
        open={
          (isTXCLTVHigherThan70 ||
            isEnabledByLO ||
            FsdAndConfScorePassed() !== true) &&
          appraisalPending &&
          openDialogAppraisal &&
          isCreditVerificationSuccess &&
          homeValuationSuccess &&
          isBorrower &&
          !isModalOpen
        }
        onAccept={() => {
          setOpenDialogAppraisal(false);
          setIsModalOpen(false);
        }}
      />

      <CUploadDocumentDialog
        open={addTaxReturnsDialogOpen}
        handleClose={() => setAddTaxReturnsDialogOpen(false)}
        taxReturns={taxReturns}
        isBorrower={isBorrower}
      />

      <CDocUploadDocumentDialog
        open={addDocsDialogOpen}
        handleClose={() => setAddDocsDialogOpen(false)}
        neededDocs={neededDocs}
        isBorrower={isBorrower}
      />
    </CTrackerTimelineItem>
  );
};

export default IncomeVerification;
